import { createQueryClient } from '@Core/clients/queryClient';
import { AiBlockErrorBoundary } from '@Pages/pageRuntime/compiler/AiBlockErrorBoundary';
import { AiComponentCompileError } from '@Pages/pageRuntime/compiler/AiComponentCompileError';
import type { PageRuntimeSourceKind } from '@Pages/pageRuntime/compiler/compileAiComponent';
import {
  diagnosticLocation,
  type EditorTransformDiagnostic
} from '@Pages/pageRuntime/compiler/editorTransformProtocol';
import { usePageSdkErrorReporter } from '@Pages/pageRuntime/compiler/PageSdkErrorReportingContext';
import { PageSdkErrorReportingProvider } from '@Pages/pageRuntime/compiler/PageSdkErrorReportingProvider';
import {
  pageSdkErrorKind,
  type PageSdkErrorKind,
  type PageSdkRenderSurface
} from '@Pages/pageRuntime/compiler/reportPageSdkError';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { PageAuthContext } from '@Pages/pageRuntime/context/PageAuthContext';
import { SdkResourceResolverContext } from '@Pages/pageRuntime/context/SdkResourceResolverContext';
import { WorkflowRouteSettingsContext } from '@Pages/pageRuntime/context/WorkflowRouteSettingsContext';
import { resolveTaggedElementInfo } from '@Pages/pageRuntime/designMode/sourceMetadata';
import { loadWorkspaceModule } from '@Pages/pageRuntime/modules/loadWorkspaceModule';
import { bindPageHostSdk } from '@Pages/pageRuntime/modules/pageHost';
import { PageRuntimeProvider, type PageRuntimeInputs } from '@Pages/pageRuntime/PageRuntimeContext';
import { usePageRuntime, type PageTransformMode } from '@Pages/pageRuntime/pageRuntimeContextValue';
import { prepareOptionalRuntime } from '@Pages/pageRuntime/prepareOptionalRuntime';
import {
  pageRuntimeRootKey,
  type AuthoringRuntime,
  type PageRuntimeRootKind
} from '@Pages/pageRuntime/useEditorPreviewGeneration';
import { QueryClientContext, QueryClientProvider } from '@tanstack/react-query';
import AlertTriangle from 'lucide-react/dist/esm/icons/triangle-alert';
import React, {
  lazy,
  Suspense,
  use,
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
  useState
} from 'react';

type PageRendererProps = PageRuntimeInputs & {
  code: string | null | undefined;
  /**
   * The import map specifier of a prebuilt workspace module. When set, the
   * renderer loads its default export instead of compiling `code`.
   */
  module?: PageRendererModule | null;
  componentProps?: Record<string, unknown>;
  children?: React.ReactNode;
  sourceKind?: PageRuntimeSourceKind;
  authoringSourcePath?: string;
  sourcePath?: string;
  renderSurface?: PageSdkRenderSurface;
  /**
   * Whether the document this renderer mounts into takes its Tailwind CSS from
   * the preview worker. A standalone renderer inside an admin shell (the
   * component editor) does not: that shell loads the browser runtime.
   */
  compilePreviewStylesheet?: boolean;
  onRendered?: () => void;
  designMode?: {
    isAvailable: boolean;
    onCodeChange?: (code: string) => void;
    enabled?: boolean;
    onEnabledChange?: (enabled: boolean) => void;
    pickImage?: () => Promise<string | null>;
    canOpenImportedSources?: boolean;
  };
};

type PageRendererModule = {
  specifier: string;
  features: readonly string[];
};

type PageRendererContentProps = PageRendererProps & {
  isEditorPreview: boolean;
};

const PageDesignMode = lazy(async () => {
  const mod = await import('@Pages/pageRuntime/designMode/PageDesignMode');

  return { default: mod.PageDesignMode };
});

function PageMessage({
  variant = 'empty',
  children
}: {
  variant?: 'empty' | 'error';
  children: React.ReactNode;
}) {
  const isError = variant === 'error';

  return (
    <div
      className={
        isError
          ? 'rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive'
          : 'rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground'
      }
    >
      {children}
    </div>
  );
}

function PageErrorFallback({
  error,
  kind,
  isEditorPreview,
  onRetry,
  failureKey,
  shouldReport = false
}: {
  error: Error;
  kind: PageSdkErrorKind;
  isEditorPreview: boolean;
  onRetry?: () => void;
  failureKey: unknown;
  shouldReport?: boolean;
}) {
  const alertRef = useRef<HTMLDivElement>(null);
  const reportedFailureKey = useRef<unknown>(null);
  const reportPageSdkError = usePageSdkErrorReporter();
  const reportError = useEffectEvent(() => {
    reportPageSdkError(error, { source: 'page' });
  });

  useEffect(() => {
    alertRef.current?.focus();

    if (shouldReport && !Object.is(reportedFailureKey.current, failureKey)) {
      reportedFailureKey.current = failureKey;
      reportError();
    }
  }, [failureKey, shouldReport]);

  const isCompileError = kind === 'compile';

  return (
    <section className="flex min-h-[280px] w-full items-center justify-center p-6">
      <div
        ref={alertRef}
        role="alert"
        data-page-sdk-error={kind}
        tabIndex={-1}
        className="w-full max-w-lg rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm"
      >
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="size-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1 space-y-4">
            <div className="space-y-1">
              <h2 className="text-base font-semibold">
                {isCompileError ? "This page couldn't be built." : "This page couldn't load."}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isCompileError
                  ? 'The page has a code problem. It will be available after the code is updated.'
                  : 'An unexpected error interrupted the page. Try loading it again.'}
              </p>
            </div>
            {!isCompileError && onRetry ? (
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            {isEditorPreview ? (
              <details className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <summary className="cursor-pointer font-medium text-foreground">
                  Error details
                </summary>
                <p className="mt-2 break-words text-destructive">{error.message}</p>
                {error.stack ? (
                  <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-muted-foreground">
                    {error.stack}
                  </pre>
                ) : null}
              </details>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Renders the page's compiled source on this component's own fiber instead of
 * as an element of its own. Compiling evaluates the source through
 * `new Function`, so every compile returns a fresh component type, and React
 * replaces an element whose type changed — discarding the running page with it,
 * down to a half-filled form or the step a login form had reached. Calling the
 * compiled function keeps one fiber, and the page's state with it, while its
 * data, identity and route catalogs change.
 *
 * The compiled source decides which hooks that fiber holds, so a changed source
 * must not reuse it — hence the source key at the call site, which mounts a
 * fresh fiber rather than reordering this one's hooks.
 */
function CompiledPage({
  component: Component,
  componentProps
}: {
  component: React.FunctionComponent<Record<string, unknown>>;
  componentProps: Record<string, unknown>;
}) {
  // The compiled source's hooks belong to this fiber, so it has to run on every
  // render of it — a memoized result would skip the hook calls the fiber holds.
  'use no memo';

  return Component(componentProps);
}

/**
 * Effects run only for a commit that completed, so this sibling reports whether
 * the compiled page rendered without throwing. A recovery remount that throws
 * again never reaches this, which is what stops the remount from repeating.
 */
function CompiledPageCommitProbe({ onCommit }: { onCommit: () => void }): null {
  useEffect(onCommit);

  return null;
}

/**
 * React enforces hook order only against a previous render that mounted at
 * least one hook, so the compiled source can violate the rules of hooks in a
 * way that surfaces only when a data change reshapes a non-empty hook
 * sequence. On the shared page fiber that violation throws instead of
 * remounting.
 */
function isHookOrderInvariantError(error: Error): boolean {
  // A production React build replaces the message with its error code, so the
  // built app only ever sees `Minified React error #300` (fewer hooks than
  // expected) or `#310` (more hooks than the previous render).
  return /Rendered (?:more|fewer) hooks than|Minified React error #(?:300|310)\b/.test(
    error.message
  );
}

/**
 * The root a renderer registers with the authoring runtime. `sourceKind` is
 * declared over every compilable source, but only a page and a layout are ever
 * rendered by a `PageRenderer` — a shared component reaches the tree through
 * `Sdk.Custom`.
 */
function rootKindOf(sourceKind: PageRuntimeSourceKind): PageRuntimeRootKind {
  return sourceKind === 'layout' ? 'layout' : 'page';
}

function diagnosticCompileError(
  diagnostic: EditorTransformDiagnostic,
  cause: Error | null
): AiComponentCompileError {
  const location = diagnosticLocation(diagnostic);

  return new AiComponentCompileError(
    location ? `${diagnostic.message} (${location})` : diagnostic.message,
    {
      ...(cause ? { cause } : {}),
      sourcePath: diagnostic.sourcePath,
      sourceLabel: diagnostic.sourceLabel,
      line: diagnostic.line,
      column: diagnostic.column
    }
  );
}

function PageRendererContent({
  code,
  module,
  auth,
  workflowRouteSettings,
  componentProps,
  children,
  isEditorPreview,
  sourceKind = 'page',
  authoringSourcePath,
  sourcePath
}: PageRendererContentProps) {
  const reportPageSdkError = usePageSdkErrorReporter();
  const runtime = usePageRuntime();
  const [hookRecoveryEpoch, setHookRecoveryEpoch] = useState(0);
  const compiledPageCommitted = useRef(true);
  const authoring = runtime?.transformMode === 'worker' ? runtime.authoring : null;
  const rootKind = rootKindOf(sourceKind);
  const rootPath = sourcePath ?? '';

  useEffect(() => {
    if (!authoring) {
      return;
    }

    authoring.registerRootSource(rootKind, rootPath, code ?? null);

    return () => authoring.releaseRootSource(rootKind, rootPath);
  }, [authoring, code, rootKind, rootPath]);

  const committed = authoring?.committedRoot(rootKind, rootPath) ?? null;
  // A pending or failed edit renders the last generation that built, so the
  // code driving this fiber is the committed one, never the draft. A prebuilt
  // module is keyed by its specifier.
  const renderedCode = module ? module.specifier : authoring ? (committed?.code ?? null) : code;

  if (!module) {
    prepareOptionalRuntime([renderedCode]);
  }

  if (!runtime) {
    throw new Error('PageRenderer requires a page runtime provider.');
  }

  let Component: React.FunctionComponent<Record<string, unknown>> | null = null;
  let error: Error | null = null;

  if (module) {
    bindPageHostSdk(runtime.Sdk);
    ({ Component, error } = use(loadWorkspaceModule(module.specifier, module.features)));
  } else if (runtime.transformMode === 'worker') {
    if (!committed) {
      // Nothing has ever built for this root. Whichever root owns the diagnostic
      // reports it, so a broken layout and the page inside it do not both raise
      // a fallback.
      if (
        runtime.authoring.diagnostic &&
        runtime.authoring.failedRootKey === pageRuntimeRootKey(rootKind, rootPath)
      ) {
        return (
          <PageErrorFallback
            error={diagnosticCompileError(
              runtime.authoring.diagnostic,
              runtime.authoring.diagnosticError
            )}
            kind="compile"
            isEditorPreview={isEditorPreview}
            failureKey={runtime.authoring.diagnostic}
            shouldReport
          />
        );
      }

      return null;
    }

    ({ Component, error } = committed.compiled);
  } else {
    ({ Component, error } = runtime.compile(code, sourceKind, {
      ...(authoringSourcePath ? { authoringFilePath: authoringSourcePath } : {}),
      ...(sourcePath ? { sourcePath } : {})
    }));
  }

  if (error) {
    return (
      <PageErrorFallback
        error={error}
        kind={module ? 'runtime' : 'compile'}
        isEditorPreview={isEditorPreview}
        failureKey={renderedCode}
        shouldReport
      />
    );
  }

  if (!Component) {
    return <PageMessage>No code to render yet.</PageMessage>;
  }

  const renderedComponentProps = {
    ...componentProps,
    ...(children === undefined ? {} : { children })
  };

  // The compiled fiber's identity: `sourcePath` separates two sources that
  // hold identical code (the component editor switches sources with
  // `preserveState: true`), and the recovery epoch forces one fresh mount when
  // a hook-order invariant error is caught below.
  const compiledPageKey = `${sourcePath ?? ''}\u0000${hookRecoveryEpoch}\u0000${renderedCode}`;

  return (
    <WorkflowRouteSettingsContext.Provider value={workflowRouteSettings ?? null}>
      <PageAuthContext.Provider value={auth ?? null}>
        <SdkResourceResolverContext.Provider value={runtime.Sdk.resourceOrNull}>
          <AiBlockErrorBoundary
            key={compiledPageKey}
            source="page"
            resetKey={componentProps}
            onError={(renderError, errorInfo) => {
              reportPageSdkError(renderError, {
                source: 'page',
                componentStack: errorInfo.componentStack
              });

              // A fresh mount has no previous hook sequence, so remounting once
              // renders the page, at the cost of its state. A mount
              // that throws again keeps the fallback rather than remounting on
              // every error, which would run the page out of React's update depth.
              if (isHookOrderInvariantError(renderError) && compiledPageCommitted.current) {
                compiledPageCommitted.current = false;
                setHookRecoveryEpoch((epoch) => epoch + 1);
              }
            }}
            renderFallback={({ error: renderError, reset }) => (
              <PageErrorFallback
                error={renderError}
                kind={pageSdkErrorKind(renderError)}
                isEditorPreview={isEditorPreview}
                onRetry={reset}
                failureKey={renderError}
              />
            )}
          >
            <CompiledPageCommitProbe
              onCommit={() => {
                compiledPageCommitted.current = true;
              }}
            />
            <CompiledPage
              key={compiledPageKey}
              component={Component}
              componentProps={renderedComponentProps}
            />
          </AiBlockErrorBoundary>
        </SdkResourceResolverContext.Provider>
      </PageAuthContext.Provider>
    </WorkflowRouteSettingsContext.Provider>
  );
}

function PageRendererWithRuntime(
  props: PageRendererContentProps & {
    transformMode: PageTransformMode;
    compilePreviewStylesheet: boolean;
  }
) {
  const runtime = usePageRuntime();
  const body = (
    <>
      <PageRendererContent {...props} />
      <PageRenderedReporter
        code={props.module?.specifier ?? props.code}
        sourceKind={props.sourceKind}
        sourcePath={props.sourcePath}
        onRendered={props.onRendered}
      />
    </>
  );

  if (runtime) {
    return body;
  }

  return (
    <PageRuntimeProvider
      rootSources={[props.code]}
      data={props.data}
      files={props.files}
      fileHosts={props.fileHosts}
      eagerImages={props.eagerImages}
      runtimeKey={props.runtimeKey ?? props.sourcePath}
      resources={props.resources}
      resourceIdsBySlug={props.resourceIdsBySlug}
      authoringSourcePath={props.authoringSourcePath}
      workspace={props.workspace}
      workspaceFields={props.workspaceFields}
      navigation={props.navigation}
      identity={props.identity}
      auth={props.auth}
      sharedComponents={props.sharedComponents}
      workflowRouteSettings={props.workflowRouteSettings}
      pageRouteSettings={props.pageRouteSettings}
      requestTracker={props.requestTracker}
      router={props.router}
      transformMode={props.transformMode}
      compilePreviewStylesheet={props.compilePreviewStylesheet}
    >
      {body}
    </PageRuntimeProvider>
  );
}

/**
 * The key that decides when a rendered page is worth reporting: the source the
 * fiber below actually rendered. In worker mode that is the committed
 * generation, and `null` until one exists — the preview's loading cover stays
 * over a frame that has nothing in it yet.
 */
function renderedReportKey(
  authoring: AuthoringRuntime | null,
  rootKind: PageRuntimeRootKind,
  rootPath: string,
  code: string | null | undefined
): string | null | undefined {
  if (!authoring) {
    return code;
  }

  if (authoring.status === 'initializing') {
    return null;
  }

  const committed = authoring.committedRoot(rootKind, rootPath);

  return `${committed?.code ?? ''}\u0000${committed ? '' : (authoring.diagnostic?.message ?? '')}`;
}

// Sits inside the Suspense boundary next to the page body, so its effect can
// only run on a commit the body took part in. A caller above the boundary
// cannot tell a suspended page from a rendered one, and the host page waits on
// this report to know the page is really on screen — print readiness reads it.
function PageRenderedReporter({
  code,
  sourceKind = 'page',
  sourcePath,
  onRendered
}: {
  code: string | null | undefined;
  sourceKind?: PageRuntimeSourceKind;
  sourcePath?: string;
  onRendered: (() => void) | undefined;
}) {
  const runtime = usePageRuntime();
  const reportKey = renderedReportKey(
    runtime?.transformMode === 'worker' ? runtime.authoring : null,
    rootKindOf(sourceKind),
    sourcePath ?? '',
    code
  );
  const reportRendered = useEffectEvent(() => onRendered?.());

  useEffect(() => {
    if (reportKey !== null) {
      reportRendered();
    }
  }, [reportKey]);

  return null;
}

function PageRendererQueryClientProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export function PageRenderer({
  files,
  runtimeKey,
  code,
  module,
  data,
  resources,
  resourceIdsBySlug,
  workspace,
  workspaceFields,
  navigation,
  identity,
  auth,
  sharedComponents,
  workflowRouteSettings,
  pageRouteSettings,
  componentProps,
  children,
  sourceKind = 'page',
  authoringSourcePath,
  sourcePath,
  renderSurface = 'published',
  compilePreviewStylesheet = false,
  onRendered,
  requestTracker,
  router,
  designMode
}: PageRendererProps) {
  const parentQueryClient = useContext(QueryClientContext);
  const preview = renderSurface === 'editor-preview';
  // A standalone renderer only goes to the worker for the authoring preview,
  // which transforms unsaved source and can wait on a round trip. A document
  // rendered under `PageLayout` takes its mode from that layout's provider
  // instead, which also sends the shareable token draft preview and any page
  // the editor frames to the worker.
  const transformMode: PageTransformMode = preview ? 'worker' : 'main-thread';
  const rendered = (
    <Suspense fallback={null}>
      <PageRendererWithRuntime
        code={code}
        module={module}
        data={data}
        files={files}
        runtimeKey={runtimeKey}
        resources={resources}
        resourceIdsBySlug={resourceIdsBySlug}
        workspace={workspace}
        workspaceFields={workspaceFields}
        navigation={navigation}
        identity={identity}
        auth={auth}
        sharedComponents={sharedComponents}
        workflowRouteSettings={workflowRouteSettings}
        pageRouteSettings={pageRouteSettings}
        componentProps={componentProps}
        isEditorPreview={preview}
        sourceKind={sourceKind}
        authoringSourcePath={authoringSourcePath}
        sourcePath={sourcePath}
        requestTracker={requestTracker}
        router={router}
        transformMode={transformMode}
        compilePreviewStylesheet={compilePreviewStylesheet}
        onRendered={onRendered}
      >
        {children}
      </PageRendererWithRuntime>
    </Suspense>
  );
  const queryClientRendered = parentQueryClient ? (
    rendered
  ) : (
    <PageRendererQueryClientProvider>{rendered}</PageRendererQueryClientProvider>
  );

  let content = queryClientRendered;

  if (designMode && designMode.enabled !== false && authoringSourcePath) {
    content = (
      <Suspense fallback={queryClientRendered}>
        <PageDesignMode
          code={code ?? ''}
          isAvailable={designMode.isAvailable}
          sharedComponents={sharedComponents}
          onCodeChange={designMode.onCodeChange}
          resolveElementInfo={resolveTaggedElementInfo}
          editableSourcePath={authoringSourcePath}
          enabled={designMode.enabled}
          onEnabledChange={designMode.onEnabledChange}
          pickImage={designMode.pickImage}
          canOpenImportedSources={designMode.canOpenImportedSources}
        >
          {queryClientRendered}
        </PageDesignMode>
      </Suspense>
    );
  }

  return (
    <PageSdkErrorReportingProvider
      compileFailureScope={code}
      renderSurface={renderSurface}
      sourcePath={sourcePath}
    >
      {content}
    </PageSdkErrorReportingProvider>
  );
}
